// AI-mode device capability map (editable). Defines, per connected device:
//   - label:       friendly name shown on the Action panel's hardware tab
//   - accessories: { <accessoryType>: [validPortNames] }  (accessory-first ->
//                   only these ports are offered; STRICT: a device not listed
//                   here exposes no ports)
//   - servos:      robot smart/PWM servos (Humanoid/Hexapod/Klaw), each with the
//                   packet protocol to build at Execute time.
//
// This map is intentionally standalone (not derived from the Assembly port
// engine) so it can be tuned/extended for Web AI without touching that logic.
// Servo protocols:
//   'sm'   -> smart servo, packet [83,77,id, MSB,LSB] (16-bit position). min/max
//             are raw 16-bit ticks; tune per robot/servo.
//   'klaw' -> Klaw servo, packet [76,87,83, klawPort, round(0.6*value)]. `pdKey`
//             indexes klawLiveServoPortsNumber (6..10 -> port 13..17).

export const ACCESSORY_LABELS = {
    led: 'LED 1C',
    led_1c: 'LED 2C',
    dc_motor: 'DC Motor',
    // geared_motor: 'Geared Motor',
    mini_geared_motor: 'Mini Geared Motor',
    BO_Motor: 'BO Motor',
    dynamex_motor: 'Dynamex Motor',
    servo_motor: 'Servo 180',
    servo_motor_270: 'Servo 270',
    servo_motor_sg90: 'Servo SG90',
    servo_motor_360: 'Servo 360',
    PWM_3_Pin_Output: 'PWM 3-Pin Output',
}

export const DEVICE_LABELS = {
    Ace: 'PeeCee',
    Ace0: 'PeeCee',
    Ace1: 'PeeCee',
    Roboki: 'Roboki',
    Poki: 'Poki',
    Toki: 'Toki',
    Moki: 'Moki',
    Humanoid: 'Zing',
    Hexapod: 'Crawl-e',
    Klaw: 'Klaw',
}

const ALL_ACE_PORTS = [
    'A1',
    'A2',
    'B1',
    'B2',
    'C1',
    'C2',
    'D1',
    'D2',
    'E1',
    'E2',
    'F1',
    'F2',
]
// Per the PeeCee external-port table: motors use the driver port GROUPS plus
// the dedicated motor ports; servos use every 2-pin socket plus the dedicated
// servo ports S1/S2; LEDs/laser use the individual sockets.
// Each motor port is a full H-bridge driver (two firmware pins), like a group
// socket: A = pins A1/A2. The two dedicated drivers are labelled M1 (firmware
// pins M1/M2) and M2 (firmware pins M3/M4).
const ACE_MOTOR_PORTS = ['A', 'B', 'C', 'D', 'E', 'F', 'M1', 'M2']
const ACE_SERVO_PORTS = [...ALL_ACE_PORTS, 'S1', 'S2']

// A standard board's accessory->ports, given its socket list and motor ports.
const boardAccessories = (ports, motorPorts) => ({
    led: ports,
    led_1c: ports,
    dc_motor: motorPorts,
    // geared_motor: motorPorts,
    mini_geared_motor: motorPorts,
    dynamex_motor: motorPorts,
    servo_motor: ports,
    servo_motor_270: ports,
    servo_motor_sg90: ports,
    servo_motor_360: ports,
})

// Smart servos 1..n with per-id range and a per-id DEFAULT (rest) position that
// the action panel prefills. Falls back to the range midpoint when no default is
// given.
const smartServos = (count, ranges = {}, defaults = {}) =>
    Array.from({ length: count }, (_, i) => {
        const id = i + 1
        const r = ranges[id] || { min: 800, max: 3200 }
        const def =
            defaults[id] != null
                ? defaults[id]
                : Math.round((r.min + r.max) / 2)
        return {
            id,
            name: `Servo ${id}`,
            proto: 'sm',
            min: r.min,
            max: r.max,
            default: def,
        }
    })

// Per-servo 16-bit limits for the smart-servo robots, from the app's
// getMinRangeV1 (Humanoid/ActionPanel.jsx). Each servo has its own range — a
// single 800-3200 for all was wrong (bugs 59/60). Crawl-e reuses these.
const HUMANOID_SERVO_RANGES = {
    1: { min: 1600, max: 2900 },
    2: { min: 400, max: 3600 },
    3: { min: 400, max: 3600 },
    4: { min: 100, max: 3300 },
    5: { min: 1600, max: 3500 },
    6: { min: 500, max: 3600 },
    7: { min: 500, max: 3600 },
    8: { min: 1, max: 4095 },
    9: { min: 1200, max: 2400 },
    10: { min: 500, max: 3700 },
    11: { min: 500, max: 3700 },
    12: { min: 800, max: 4000 },
    13: { min: 500, max: 2500 },
    14: { min: 500, max: 3500 },
    15: { min: 500, max: 3500 },
    16: { min: 1, max: 4095 },
}

// Per-servo 16-bit limits for Crawl-e (Hexapod), from PORTDATA.HEXAPOD1 in
// Humanoid/defaultData.js — each of the 18 servos has its own range (bug 60).
const HEXAPOD_SERVO_RANGES = {
    1: { min: 400, max: 3700 },
    2: { min: 440, max: 3670 },
    3: { min: 300, max: 4095 },
    4: { min: 500, max: 3700 },
    5: { min: 440, max: 3730 },
    6: { min: 320, max: 4095 },
    7: { min: 360, max: 3670 },
    8: { min: 440, max: 3730 },
    9: { min: 420, max: 4095 },
    10: { min: 360, max: 3700 },
    11: { min: 400, max: 3580 },
    12: { min: 0, max: 3760 },
    13: { min: 360, max: 3620 },
    14: { min: 360, max: 3610 },
    15: { min: 0, max: 3760 },
    16: { min: 360, max: 3670 },
    17: { min: 360, max: 3570 },
    18: { min: 0, max: 3660 },
}

// Default (rest) servo positions, prefilled in the action panel — from the
// `value` field of each servo in PORTDATA (Humanoid/defaultData.js). Version
// matters: Zing v1/2/3 and Crawl-e use raw 16-bit ticks; Zing v0 uses an old
// 0-1000 position scale (kept separate below).
const HUMANOID_SERVO_DEFAULTS = {
    1: 2048, 2: 1585, 3: 2048, 4: 2428, 5: 2048, 6: 2355, 7: 3276, 8: 2965,
    9: 2048, 10: 2506, 11: 2048, 12: 1662, 13: 2048, 14: 1740, 15: 819, 16: 1126,
}
const HEXAPOD_SERVO_DEFAULTS = {
    1: 2048, 2: 1277, 3: 819, 4: 2048, 5: 1277, 6: 819, 7: 2048, 8: 1277, 9: 819,
    10: 2048, 11: 2818, 12: 3276, 13: 2048, 14: 2818, 15: 3276, 16: 2048,
    17: 2818, 18: 3276,
}
// Zing v0 firmware: 0-1000 position scale with its own defaults.
const HUMANOID_V0_DEFAULTS = {
    1: 500, 2: 387, 3: 500, 4: 593, 5: 500, 6: 575, 7: 800, 8: 724, 9: 500,
    10: 612, 11: 500, 12: 406, 13: 500, 14: 425, 15: 200, 16: 275,
}
const HUMANOID_V0_RANGES = (() => {
    const r = {}
    for (let id = 1; id <= 16; id++) r[id] = { min: 0, max: 1000 }
    return r
})()

export const DEVICE_CAPABILITIES = {
    Ace: {
        label: 'PeeCee',
        accessories: {
            led: ALL_ACE_PORTS,
            led_1c: ALL_ACE_PORTS,
            dc_motor: ACE_MOTOR_PORTS,
            // geared_motor: ACE_MOTOR_PORTS,
            mini_geared_motor: ACE_MOTOR_PORTS,
            dynamex_motor: ACE_MOTOR_PORTS,
            servo_motor: ACE_SERVO_PORTS,
            servo_motor_270: ACE_SERVO_PORTS,
            servo_motor_sg90: ACE_SERVO_PORTS,
            servo_motor_360: ACE_SERVO_PORTS,
        },
        servos: [],
    },
    Roboki: {
        label: 'Roboki',
        accessories: {
            led: ['A1', 'A2', 'B1', 'B2'],
            led_1c: ['A1', 'A2', 'B1', 'B2'],
            dc_motor: ['A', 'B', 'M1', 'M2'],
            // geared_motor: ['A', 'B', 'M1', 'M2'],
            mini_geared_motor: ['A', 'B', 'M1', 'M2'],
            dynamex_motor: ['A', 'B', 'M1', 'M2'],
            servo_motor: ['A1', 'A2', 'B1', 'B2', 'S1', 'S2'],
            servo_motor_270: ['A1', 'A2', 'B1', 'B2', 'S1', 'S2'],
            servo_motor_360: ['A1', 'A2', 'B1', 'B2', 'S1', 'S2'],
        },
        servos: [],
    },
    Poki: {
        label: 'Poki',
        accessories: {
            PWM_3_Pin_Output: ['S1', 'S2', 'A1', 'A2'],
            dc_motor: ['M1', 'M2'],
            // geared_motor: ['M1', 'M2'],
            mini_geared_motor: ['M1', 'M2'],
            BO_Motor: ['M1', 'M2'],
            dynamex_motor: ['M1', 'M2'],
            servo_motor: ['A1', 'A2', 'S1', 'S2'],
            servo_motor_270: ['A1', 'A2', 'S1', 'S2'],
            servo_motor_360: ['A1', 'A2', 'S1', 'S2'],
            servo_motor_sg90: ['A1', 'A2', 'S1', 'S2'],
        },
        servos: [],
    },
    Toki: {
        label: 'Toki',
        accessories: {
            PWM_3_Pin_Output: ['S1', 'S2'],
            dc_motor: ['M1', 'M2'],
            // geared_motor: ['M1', 'M2'],
            mini_geared_motor: ['M1', 'M2'],
            BO_Motor: ['M1', 'M2'],
            dynamex_motor: ['M1', 'M2'],
            servo_motor: ['S1', 'S2'],
            servo_motor_270: ['S1', 'S2'],
            servo_motor_360: ['S1', 'S2'],
            servo_motor_sg90: ['S1', 'S2'],
        },
        servos: [],
    },
    Moki: {
        label: 'Moki',
        accessories: boardAccessories(ALL_ACE_PORTS, ['M1', 'M2']),
        servos: [],
    },
    Humanoid: {
        label: 'Zing',
        accessories: {
            led: ['A1', 'A2', 'C1', 'C2'],
            led_1c: ['A1', 'A2', 'C1', 'C2'],
            dc_motor: ['A', 'C'],
            // geared_motor: ['A', 'C'],
            servo_motor: ['A1', 'A2', 'C1', 'C2'],
            servo_motor_270: ['A1', 'A2', 'C1', 'C2'],
            servo_motor_360: ['A1', 'A2', 'C1', 'C2'],
        },
        servos: smartServos(16, HUMANOID_SERVO_RANGES, HUMANOID_SERVO_DEFAULTS),
    },
    Hexapod: {
        label: 'Crawl-e',
        accessories: {
            led: ['A1', 'A2', 'C1', 'C2'],
            led_1c: ['A1', 'A2', 'C1', 'C2'],
            dc_motor: ['A', 'C'],
            servo_motor: ['A1', 'A2', 'C1', 'C2'],
            servo_motor_270: ['A1', 'A2', 'C1', 'C2'],
            servo_motor_360: ['A1', 'A2', 'C1', 'C2'],
        },
        servos: smartServos(18, HEXAPOD_SERVO_RANGES, HEXAPOD_SERVO_DEFAULTS),
    },
    Klaw: {
        label: 'Klaw',
        accessories: {
            led: ['A1', 'A2', 'B1', 'B2'],
            led_1c: ['A1', 'A2', 'B1', 'B2'],
            dc_motor: ['A', 'B'],
            // geared_motor: ['A', 'B'],
            servo_motor: ['A1', 'A2', 'B1', 'B2'],
            servo_motor_270: ['A1', 'A2', 'B1', 'B2'],
            servo_motor_360: ['A1', 'A2', 'B1', 'B2'],
        },
        // Klaw PWM servos S1..S5 -> PORTDATA keys 6..10 -> klawLiveServoPortsNumber.
        servos: [
            {
                id: 1,
                name: 'S1 Servo',
                proto: 'klaw',
                pdKey: 6,
                min: 0,
                max: 300,
                default: 150,
            },
            {
                id: 2,
                name: 'S2 Servo',
                proto: 'klaw',
                pdKey: 7,
                min: 80,
                max: 180,
                default: 150,
            },
            {
                id: 3,
                name: 'S3 Servo',
                proto: 'klaw',
                pdKey: 8,
                min: 50,
                max: 280,
                default: 150,
            },
            {
                id: 4,
                name: 'S4 Servo',
                proto: 'klaw',
                pdKey: 9,
                min: 0,
                max: 300,
                default: 150,
            },
            {
                id: 5,
                name: 'S5 Servo',
                proto: 'klaw',
                pdKey: 10,
                min: 0,
                max: 300,
                default: 160,
            },
        ],
    },
}

// Device_key aliases -> capability key (PeeCee variants share the Ace map).
const DEVICE_ALIASES = { Ace0: 'Ace', Ace1: 'Ace' }
const capKey = (device) => DEVICE_ALIASES[device] || device

// The device to author actions against: the connected device, else the one
// chosen on AI Kit selection, else PeeCee as a sensible default.
export const authoringDevice = () =>
    sessionStorage.getItem('connectedDevice') ||
    sessionStorage.getItem('allowedDevice') ||
    'Ace'

// Friendly label for the hardware tab. Only "Hardware" when nothing is known.
export const deviceLabel = () => {
    const connected = sessionStorage.getItem('connectedDevice')
    const chosen = sessionStorage.getItem('allowedDevice')
    const dev = connected || chosen
    if (!dev) return 'Hardware'
    return DEVICE_LABELS[dev] || DEVICE_CAPABILITIES[capKey(dev)]?.label || dev
}

const capsFor = (device) => DEVICE_CAPABILITIES[capKey(device)] || null

// Accessory types available on a device (strict: [] if the device isn't mapped).
export const accessoriesFor = (device) => {
    const caps = capsFor(device)
    return caps ? Object.keys(caps.accessories) : []
}

// Valid ports for an accessory on a device (strict: [] when unmapped).
export const portsFor = (device, accessory) => {
    const caps = capsFor(device)
    return (caps && caps.accessories[accessory]) || []
}

// Robot servos for a device (Humanoid/Hexapod/Klaw), else []. Version-aware:
// Zing (Humanoid) v0 firmware uses a 0-1000 position scale with its own
// defaults, unlike v1/2/3 (raw 16-bit ticks); the connected device version
// selects which.
export const servosFor = (device) => {
    const caps = capsFor(device)
    if (!caps) return []
    if (
        capKey(device) === 'Humanoid' &&
        (sessionStorage.getItem('deviceVersion') || '')[0] === '0'
    )
        return smartServos(16, HUMANOID_V0_RANGES, HUMANOID_V0_DEFAULTS)
    return caps.servos || []
}

export const servoById = (device, id) =>
    servosFor(device).find((s) => String(s.id) === String(id)) || null

export const hasServos = (device) => servosFor(device).length > 0
